const SEPARATOR = '-';

const isValidString = (value?: string): value is string => {
  return typeof value === 'string' && value.trim().length > 0;
};

const getFormattedElementText = (elementText?: string): string | undefined => {
  if (!isValidString(elementText)) {
    return undefined;
  }

  return elementText
    .trim()
    .toLowerCase()
    .replace(/\s+/g, SEPARATOR)
    .replace(/[^a-z0-9-]/g, SEPARATOR)
    .replace(/-+/g, SEPARATOR)
    .replace(/^-|-$/g, '');
};

export const getTestId = (testId: string, elementText?: string): string | undefined => {
  if (!isValidString(testId)) {
    return undefined;
  }
  const formattedElementText = getFormattedElementText(elementText);

  return testId && formattedElementText ? `${testId}${SEPARATOR}${formattedElementText}` : testId;
};

export const TestIds = {
  login: {
    form: 'login-form',
    emailInput: 'login-email-input',
    passwordInput: 'login-password-input',
    passwordToggle: 'login-password-toggle',
    submitButton: 'login-submit-button',
    ssoButton: 'login-sso-button',
    ssoLinkHint: 'login-sso-link-hint',
    moreSignInOptions: 'login-more-sign-in-options',
    secondaryAuthSection: 'login-secondary-auth-section',
    forgotPasswordLink: 'login-forgot-password-link',
    registerLink: 'login-register-link',
    errorMessage: 'login-error-message',
    logo: 'login-logo'
  },
  registration: {
    form: 'registration-form',
    logo: 'registration-logo',
    firstNameInput: 'registration-first-name-input',
    lastNameInput: 'registration-last-name-input',
    emailInput: 'registration-email-input',
    organizationNameInput: 'registration-organization-name-input',
    defaultLanguageSelect: 'registration-default-language-select',
    submitButton: 'registration-submit-button',
    loginLink: 'registration-login-link'
  },
  nav: {
    base: 'nav',
    sidebar: 'nav-sidebar',
    profileLink: 'nav-profile-link'
  },
  layout: {
    backButton: 'layout-back-button',
    hamburgerButton: 'layout-hamburger-button',
    profileButtonDesktop: 'layout-profile-button-desktop',
    profileButtonMobile: 'layout-profile-button-mobile',
    logoutButton: 'layout-logout-button'
  },
  organizations: {
    prefix: 'organizations',
    page: 'organizations-page'
  },
  users: {
    prefix: 'users',
    page: 'users-page'
  },
  events: {
    prefix: 'events',
    page: 'events-page',
    tab: 'event-tab',
    publishButton: 'event-publish-button',
    noCeremoniesAssigned: 'events-no-ceremonies-assigned'
  },
  devices: {
    prefix: 'devices',
    page: 'devices-page'
  },
  errorPage: {
    root: 'error-page',
    goBack: 'error-page-go-back'
  },
  common: {
    tablePrefix: 'table'
  },
  coverPhotos: {
    // index.tsx — list page
    page: 'cover-photos-page',
    createButton: 'cover-photos-create-button',
    counter: 'cover-photos-counter',
    zeroState: 'cover-photos-zero-state',
    // CreateEditCoverPhoto.tsx — create/edit page
    form: 'cover-photo-form',
    saveButton: 'cover-photo-save-button',
    profileBorderToggle: 'cover-photo-profile-border-toggle',
    backgroundSelect: 'cover-photo-background-select',
    // CoverPhotoCanvas.tsx
    canvasContainer: 'cover-photo-canvas-container',
    canvasLockButton: 'cover-photo-canvas-lock-button',
    // CoverPhotoAssetPanel.tsx
    assetPanel: 'cover-photo-asset-panel',
    assetImage: 'cover-photo-asset-image',
    deleteAssetButton: 'cover-photo-delete-asset-button',
    choosePhotoButton: 'cover-photo-choose-photo-button',
    uploadButton: 'cover-photo-upload-button',
    // AssetsModal.tsx
    assetsModalGrid: 'cover-photo-assets-modal-grid',
    // Exposure slider (ValueSlider in CreateEditCoverPhoto)
    exposureSlider: 'cover-photo-exposure-slider',
    // TextEditor.tsx
    textEditor: 'cover-photo-text-editor',
    textEditorAlignLeft: 'text-editor-align-left',
    textEditorAlignCenter: 'text-editor-align-center',
    textEditorAlignRight: 'text-editor-align-right',
    textEditorFontSelect: 'text-editor-font-select',
    textEditorFontSizeSelect: 'text-editor-font-size-select',
    textEditorColorPicker: 'text-editor-color-picker',
    // AssetItem.tsx — individual asset card in the grid
    assetItemCard: 'cover-photo-asset-item-card'
  },
  eventMusic: {
    searchInput: 'event-music-search-input',
    uploadButton: 'event-music-upload-button',
    searchResults: 'event-music-search-results',
    searchResultRow: 'event-music-search-result-row',
    searchResultPreviewButton: 'event-music-search-result-preview-button',
    searchResultAddButton: 'event-music-search-result-add-button',
    player: 'event-music-player',
    chosenSongs: 'event-music-chosen-songs',
    chosenSongItem: 'event-music-chosen-song-item',
    chosenSongEditButton: 'event-music-chosen-song-edit-button',
    trimModal: 'event-music-trim-modal',
    trimDurationLabel: 'event-music-trim-duration-label',
    trimTrack: 'event-music-trim-track',
    trimStartHandle: 'event-music-trim-start-handle',
    trimEndHandle: 'event-music-trim-end-handle',
    trimSaveButton: 'event-music-trim-save-button'
  },
  uploadContent: {
    assetItemEditButton: 'upload-content-asset-item-edit-button',
    videoTrimModal: 'upload-content-video-trim-modal',
    videoTrimDurationLabel: 'upload-content-video-trim-duration-label',
    videoTrimTrack: 'upload-content-video-trim-track',
    videoTrimStartHandle: 'upload-content-video-trim-start-handle',
    videoTrimEndHandle: 'upload-content-video-trim-end-handle',
    videoTrimSaveButton: 'upload-content-video-trim-save-button'
  },
  eventTextOverlay: {
    page: 'event-text-overlay-page',
    createButton: 'event-text-overlay-create-button',
    zeroState: 'event-text-overlay-zero-state',
    grid: 'event-text-overlay-grid',
    itemCard: 'event-text-overlay-item-card',
    editButton: 'event-text-overlay-edit-button',
    deleteButton: 'event-text-overlay-delete-button',
    previewButton: 'event-text-overlay-preview-button'
  },
  eventCeremony: {
    page: 'event-ceremony-page',
    deviceSelect: 'event-ceremony-device-select',
    templateSelect: 'event-ceremony-template-select',
    slotList: 'event-ceremony-slot-list',
    slotItem: 'event-ceremony-slot-item',
    addSlotButton: 'event-ceremony-add-slot-button',
    slotTypeSelect: 'event-ceremony-slot-type-select',
    saveSlotButton: 'event-ceremony-save-slot-button',
    assetFilterButton: 'event-ceremony-asset-filter-button',
    assetItemCard: 'event-ceremony-asset-item-card'
  },
  digitalSignage: {
    page: 'digital-signage-page',
    screensList: 'digital-signage-screens-list',
    deviceSearchInput: 'digital-signage-device-search-input',
    deviceFilterButton: 'digital-signage-device-filter-button',
    screenCard: 'digital-signage-screen-card',
    calendarPanel: 'digital-signage-calendar-panel',
    contentSetupPanel: 'digital-signage-content-setup-panel',
    monthCalendar: 'digital-signage-month-calendar',
    dayCalendar: 'digital-signage-day-calendar',
    scheduleEvent: 'digital-signage-schedule-event',
    contentSetupTabs: 'digital-signage-content-setup-tabs',
    contentSetupTabDefault: 'digital-signage-content-setup-tab-default',
    contentSetupTabPlaylist: 'digital-signage-content-setup-tab-playlist',
    contentSetupTabPush: 'digital-signage-content-setup-tab-push',
    contentSetupEditButton: 'digital-signage-content-setup-edit-button',
    contentSetupDeleteButton: 'digital-signage-content-setup-delete-button',
    contentSetupRepeatUntilButton: 'digital-signage-content-setup-repeat-until-button',
    repeatSlotModal: 'digital-signage-repeat-slot-modal',
    repeatSlotCancelButton: 'digital-signage-repeat-slot-cancel-button',
    repeatSlotAddButton: 'digital-signage-repeat-slot-add-button',
    timeSlotModal: 'digital-signage-time-slot-modal',
    timeSlotDate: 'digital-signage-time-slot-date',
    timeSlotAllDayYes: 'digital-signage-time-slot-all-day-yes',
    timeSlotAllDayNo: 'digital-signage-time-slot-all-day-no',
    timeSlotStart: 'digital-signage-time-slot-start',
    timeSlotEnd: 'digital-signage-time-slot-end',
    timeSlotCancel: 'digital-signage-time-slot-cancel',
    timeSlotSubmit: 'digital-signage-time-slot-submit'
  },
  liveStream: {
    page: 'live-stream-page',
    panelDesktop: 'live-stream-panel-desktop',
    enabledToggle: 'live-stream-enabled-toggle',
    visitButton: 'live-stream-visit-button',
    streamControls: 'live-stream-stream-controls',
    streamIdInput: 'live-stream-stream-id-input',
    streamStartStopButton: 'live-stream-stream-start-stop-button',
    streamResetButton: 'live-stream-stream-reset-button',
    shareSection: 'live-stream-share-section',
    copyPublicUrlButton: 'live-stream-copy-public-url-button',
    inviteForm: 'live-stream-invite-form',
    inviteEmailInput: 'live-stream-invite-email-input',
    inviteSubmitButton: 'live-stream-invite-submit-button',
    guestListDesktop: 'live-stream-guest-list-desktop',
    guestListMobile: 'live-stream-guest-list-mobile',
    guestInvitationItem: 'live-stream-guest-invitation-item',
    guestInvitationLinkButton: 'live-stream-guest-invitation-link-button',
    guestInvitationDeleteButton: 'live-stream-guest-invitation-delete-button',
    recordingSection: 'live-stream-recording-section',
    recordingToggle: 'live-stream-recording-toggle',
    recordingDateInput: 'live-stream-recording-date-input',
    waitingScreenSection: 'live-stream-waiting-screen-section',
    waitingScreenEditDescriptionButton: 'live-stream-waiting-screen-edit-description-button',
    waitingScreenChangeBackgroundSelect: 'live-stream-waiting-screen-change-background-select',
    waitingScreenPreview: 'live-stream-waiting-screen-preview',
    waitingScreenPreviewImage: 'live-stream-waiting-screen-preview-image',
    waitingScreenPreviewVideo: 'live-stream-waiting-screen-preview-video',
    waitingScreenPreviewPlaceholder: 'live-stream-waiting-screen-preview-placeholder',
    waitingScreenDescriptionOverlay: 'live-stream-waiting-screen-description-overlay',
    modalAssetsGrid: 'live-stream-modal-assets-grid',
    modalAssetItem: 'live-stream-modal-asset-item',
    modalDescriptionForm: 'live-stream-modal-description-form',
    modalDescriptionTextareaContainer: 'live-stream-modal-description-textarea-container',
    modalConfirmButton: 'live-stream-modal-confirm-button',
    permissionModal: 'live-stream-permission-modal'
  },
  tributeVideo: {
    // Setup step
    setupForm: 'tribute-video-setup-form',
    nameInput: 'tribute-video-name-input',
    videoStyleSelect: 'tribute-video-style-select',
    titleInput: 'tribute-video-title-input',
    nameOfDeceasedInput: 'tribute-video-name-of-deceased-input',
    birthDeathInput: 'tribute-video-birth-death-input',
    enableTextInVideoToggle: 'tribute-video-enable-text-toggle',
    enableMusicToggle: 'tribute-video-enable-music-toggle',
    enableIntroToggle: 'tribute-video-enable-intro-toggle',
    enableOutroToggle: 'tribute-video-enable-outro-toggle',
    enableBarsToggle: 'tribute-video-enable-bars-toggle',
    resolutionSelect: 'tribute-video-resolution-select',
    // Wizard navigation
    nextButton: 'tribute-video-next-button',
    backButton: 'tribute-video-back-button',
    // Theme step
    createCustomThemeButton: 'tribute-video-create-custom-theme-button',
    customThemeNameInput: 'tribute-video-custom-theme-name-input',
    addCustomThemeButton: 'tribute-video-add-custom-theme-button',
    themeCard: 'tribute-video-theme-card',
    customThemeEditButton: 'tribute-video-custom-theme-edit-button',
    customThemeBarsColorTrigger: 'tribute-video-custom-theme-bars-color-trigger',
    customThemeBarsColorHexInput: 'tribute-video-custom-theme-bars-color-hex-input',
    themeTextTimingValue: 'tribute-video-theme-text-timing-value',
    themeTextTimingAdjustButton: 'tribute-video-theme-text-timing-adjust-button',
    // Photos step
    browseUploadedPhotosButton: 'tribute-video-browse-uploaded-photos-button',
    uploadedAssetsModalScroll: 'tribute-video-uploaded-assets-scroll',
    uploadedAssetsModalGrid: 'tribute-video-uploaded-assets-grid',
    uploadedAssetItem: 'tribute-video-uploaded-asset-item',
    // Review step
    generateFullVideoButton: 'tribute-video-generate-full-video-button',
    generatePreviewButton: 'tribute-video-generate-preview-button'
  },
  mfa: {
    verifyForm: 'mfa-verify-form',
    verifyOtpInput: 'mfa-verify-otp-input',
    enrollOtpInput: 'mfa-enroll-otp-input',
    reAuthOtpInput: 'mfa-reauth-otp-input',
    changePasswordOtpInput: 'mfa-change-password-otp-input',
    qrImage: 'mfa-qr-image',
    settingsToggle: 'mfa-settings-toggle',
    setupEnableButton: 'mfa-setup-enable-button',
    setupSkipButton: 'mfa-setup-skip-button'
  },
  supportChat: {
    launcher: 'support-chat-launcher',
    helpButton: 'support-chat-help-button',
    menuItem: 'support-chat-menu-item',
    panel: 'support-chat-panel',
    message: 'support-chat-message',
    input: 'support-chat-input',
    sendButton: 'support-chat-send-button',
    talkToPersonButton: 'support-chat-talk-to-person-button',
    escalateSubmitButton: 'support-chat-escalate-submit-button',
    escalationNotice: 'support-chat-escalation-notice',
    newConversationButton: 'support-chat-new-conversation-button'
  }
} as const;
